import { useEffect, useState } from 'react'
import { Handshake, Plus, X } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { adminMutate, Select } from '../admin/primitives.js'
import { Field, NumberInput, SwitchRow, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { Link } from '../router.js'
import { useLecture } from './associations-api.js'
import { Chargement, Erreur } from './AssociationsCommun.js'
import {
  CHEMIN_LISTE, LIBELLES_MODE, REGLAGES_DEFAUT, ajouterMot, cheminFiche, reglagesChanges, reglagesInvalides, reglagesMemes, signalerAssociations,
  type BornesReglages, type ModeReconnaissance, type ReglagesAssociations, type ReponseListe,
} from './associations-model.js'

/**
 * /settings/soirees/associations — RÉGLAGES SOIRÉES › ASSOCIATIONS (20/10/2026).
 *
 * Règle permanente : chaque nouveauté a ses réglages dans Administration.
 * Ici : ce que Gestion lie seul, les mots qu'il ne prend jamais pour un nom
 * d'association (les marques du club), ceux qui signalent une soirée
 * d'association, la fenêtre de la tendance, où le nom de l'association
 * apparaît, et ce qui est annoncé ou compté. Valeurs posées par la migration
 * 1759500000000 sans écraser l'existant ; tout passe par la Save Bar.
 *
 * Les ALIAS appartiennent à chaque association : ils se modifient sur sa
 * fiche (un geste annulable) — cette page les montre et y mène. La part de
 * l'association par billet reste dans Comptabilité › Règles.
 */

interface Reponse { settings: ReglagesAssociations; bornes: BornesReglages; droits: { gerer: boolean; argent: boolean } }

/**
 * « Activer ≠ faire » (revue du 20/10/2026) : les trois interrupteurs « Où
 * l'association apparaît » sont restés figés (« bientôt ») tant que la fiche
 * de la soirée, le calendrier et l'Accueil ne montraient pas l'association.
 * Les liens sont branchés depuis (lot 4) : chacun se manipule, sous
 * « Gérer les associations » comme les autres.
 */

export function AssociationsSettingsPage() {
  const meta = useShellMeta()
  const { etat, relire } = useLecture<Reponse>('/api/v1/settings/associations')
  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger asso-page">
        <SettingsCrumb />
        <h1 className="lc-title"><Handshake size={26} aria-hidden="true" /> Associations</h1>
        <p className="lc-subtitle">
          Comment Gestion reconnaît la soirée d’une association à son nom, ce qu’il lie seul, et où l’association apparaît.
          Les associations elles-mêmes vivent dans <Link href={CHEMIN_LISTE}>Exploitation › Associations</Link>.
        </p>
        {etat.phase === 'chargement' ? <Chargement libelle="Lecture des réglages" />
          : etat.phase === 'erreur' ? <Erreur message={etat.message} onReessayer={relire} />
            : <Formulaire r={etat.data} />}
      </main>
    </Shell>
  )
}

function Formulaire({ r }: { r: Reponse }) {
  const gerer = r.droits.gerer
  const [serveur, setServeur] = useState<ReglagesAssociations>({ ...REGLAGES_DEFAUT, ...r.settings })
  const [v, setV] = useState<ReglagesAssociations>(serveur)
  useEffect(() => { const s = { ...REGLAGES_DEFAUT, ...r.settings }; setServeur(s); setV(s) }, [r.settings])
  const [tendance, setTendance] = useState(String(serveur.trend_nights))
  useEffect(() => setTendance(String(v.trend_nights)), [v.trend_nights])

  useSaveBar({
    dirty: gerer && !reglagesMemes(v, serveur),
    count: reglagesChanges(v, serveur),
    saveLabel: 'Enregistrer les réglages',
    invalid: reglagesInvalides(v, r.bornes),
    onSave: async () => {
      const x = await adminMutate<{ ok?: boolean; settings?: ReglagesAssociations; message?: string; error?: string }>('PUT', '/api/v1/settings/associations', v)
      if (x.status !== 200 || !x.data.settings) return x.data.message ?? 'Enregistrement impossible.'
      const s = { ...REGLAGES_DEFAUT, ...x.data.settings }
      setServeur(s); setV(s)
      // La reconnaissance a pu changer : la liste, « À décider » et le badge se relisent.
      signalerAssociations()
      return true
    },
    onCancel: () => setV(serveur),
    successMessage: 'Réglages des associations enregistrés.',
  })

  return (
    <>
      <section className="lc-panel gd-panel asso-bloc" aria-labelledby="ra-reconnaissance">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ra-reconnaissance">Reconnaissance automatique</h2></header>
        <div className="lc-panel__body asso-reglages">
          <Field label="Gestion lie seul une soirée à son association"
            helper="Certain : le nom désigne l’association ET la soirée est vendue sur Pretix ou porte les formules ci-dessous. Probable : le nom seul. Le reste attend dans « À décider ». Un lien posé à la main n’est jamais retouché.">
            <Select value={v.recognition_mode} ariaLabel="Reconnaissance automatique"
              options={(Object.keys(LIBELLES_MODE) as ModeReconnaissance[]).map((k) => ({ value: k, label: LIBELLES_MODE[k] }))}
              onChange={(x) => { if (gerer) setV({ ...v, recognition_mode: x as ModeReconnaissance }) }} />
          </Field>
          <Mots titre="Mots jamais pris pour un nom d’association" aide="Les marques et les mots du club (L’ÉTUDIANTE, CAMPUS 88, « soirée ») : un alias fait de ces seuls mots est refusé."
            mots={v.excluded_words} gerer={gerer} bornes={r.bornes} onChange={(m) => setV({ ...v, excluded_words: m })} />
          <Mots titre="Formules d’une soirée d’association" aide="Lues dans le nom des produits vendus : une soirée qui en vend au moins deux est « certaine » (Essentiel, Soft Mood, Prestige). Gardez-en au moins deux : une seule (une table « Prestige » du club) ne prouve rien."
            mots={v.night_product_words} gerer={gerer} bornes={r.bornes} onChange={(m) => setV({ ...v, night_product_words: m })} />
          <Mots titre="Mots qui signalent une association sans dire laquelle" aide="« BDE », « post partiel »… : la soirée part dans « À décider » comme « association à identifier »."
            mots={v.generic_words} gerer={gerer} bornes={r.bornes} onChange={(m) => setV({ ...v, generic_words: m })} />
        </div>
      </section>

      <section className="lc-panel gd-panel asso-bloc" aria-labelledby="ra-chiffres">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ra-chiffres">Chiffres</h2></header>
        <div className="lc-panel__body asso-reglages">
          <Field label="La tendance compare la dernière soirée aux précédentes, sur"
            helper={`Entre ${r.bornes.trend_min} et ${r.bornes.trend_max} soirées. Au-delà de ±10 %, une association monte ou baisse.`}>
            <NumberInput value={tendance} min={r.bornes.trend_min} max={r.bornes.trend_max} unit="soirées" steppers ariaLabel="Soirées de la tendance"
              onChange={(x) => { if (!gerer) return; setTendance(x); const n = Number(x); if (x.trim() !== '' && Number.isFinite(n)) setV({ ...v, trend_nights: n }) }} />
          </Field>
          <SwitchRow checked={v.count_manual_nights} disabled={!gerer} onChange={(x) => setV({ ...v, count_manual_nights: x })}
            title="Compter les nuits marquées à la main"
            desc="Une nuit sans soirée nommée, attribuée à une association : elle compte parmi ses soirées et ses entrées (saisies, ou celles de la Comptabilité quand la nuit n’a eu qu’elle)." />
        </div>
      </section>

      <section className="lc-panel gd-panel asso-bloc" aria-labelledby="ra-affichage">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="ra-affichage">Où l’association apparaît</h2></header>
        <div className="lc-panel__body asso-reglages">
          <p className="lc-hint" style={{ marginTop: 0 }}>
            Le nom de l’association, toujours un lien vers sa fiche, pour qui peut consulter les associations. La Comptabilité
            le montre aussi sur la ligne de chaque événement, avec les deux droits. L’alerte « à rattacher avant la nuit » se
            règle dans <Link href="/settings/soirees/billetteries">Réglages Soirées › Billetteries</Link>.
          </p>
          <SwitchRow checked={v.show_on_soiree} disabled={!gerer} onChange={(x) => setV({ ...v, show_on_soiree: x })}
            title="Sur la fiche de la soirée" desc="« · Médecine » après chaque billetterie, l’étiquette « Soirée association » qui mène à sa fiche, et la sous-ligne de la liste des soirées." />
          <SwitchRow checked={v.show_on_calendar} disabled={!gerer} onChange={(x) => setV({ ...v, show_on_calendar: x })}
            title="Dans le calendrier" desc="Dans le panneau d’une soirée, jamais dans la case (elle est déjà un bouton)." />
          <SwitchRow checked={v.show_on_home} disabled={!gerer} onChange={(x) => setV({ ...v, show_on_home: x })}
            title="Sur l’Accueil" desc="« Prochaine soirée de Médecine : jeu. 22/10 · 249 billets », dans le panneau des soirées à venir." />
          <SwitchRow checked={v.notify_auto_links} disabled={!gerer} onChange={(x) => setV({ ...v, notify_auto_links: x })}
            title="Annoncer les liens automatiques" desc="Dans le Centre d’activité, à qui gère les associations : chaque soirée que Gestion vient de lier seul, à corriger d’un clic si besoin." />
        </div>
      </section>

      <AliasParAssociation />
    </>
  )
}

/** Une liste de mots : des puces retirables, un champ pour en ajouter. */
function Mots(props: { titre: string; aide: string; mots: string[]; gerer: boolean; bornes: BornesReglages; onChange: (m: string[]) => void }) {
  const [saisie, setSaisie] = useState('')
  const ajouter = () => { props.onChange(ajouterMot(props.mots, saisie)); setSaisie('') }
  const plein = props.mots.length >= props.bornes.mots_max
  return (
    <div className="lc-field asso-mots">
      <span className="lc-label">{props.titre}</span>
      {props.mots.length === 0 ? <span className="lc-hint">Aucun.</span> : (
        <ul className="asso-puces">
          {props.mots.map((m) => (
            <li key={m} className="asso-puce">
              {m}
              {props.gerer ? (
                <button type="button" className="asso-puce__retirer" aria-label={`Retirer « ${m} »`} onClick={() => props.onChange(props.mots.filter((x) => x !== m))}>
                  <X size={12} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {props.gerer && !plein ? (
        <div className="asso-alias__ajout">
          <TextInput value={saisie} onChange={setSaisie} maxLength={props.bornes.mot_longueur_max} ariaLabel={`Ajouter : ${props.titre}`} />
          <button type="button" className="lc-btn lc-btn--sm" disabled={!saisie.trim()} onClick={ajouter}><Plus size={14} aria-hidden="true" /> Ajouter</button>
        </div>
      ) : null}
      <span className="lc-helper">{props.aide}</span>
    </div>
  )
}

/** Les alias de chaque association : montrés ici, modifiés sur sa fiche (un geste annulable). */
function AliasParAssociation() {
  const { etat, relire } = useLecture<ReponseListe>('/api/v1/associations?statut=toutes')
  return (
    <section className="lc-panel gd-panel asso-bloc" aria-labelledby="ra-alias">
      <header className="lc-panel__head"><h2 className="lc-section-title" id="ra-alias">Alias de chaque association</h2></header>
      <div className="lc-panel__body">
        <p className="lc-hint" style={{ marginTop: 0 }}>Les mots du nom des soirées qui désignent chaque association. Ils se modifient sur sa fiche : chaque ajout ou retrait s’annule.</p>
        {etat.phase === 'chargement' ? <Chargement libelle="Lecture des alias" />
          : etat.phase === 'erreur' ? <Erreur message={etat.message} onReessayer={relire} />
            : (
              <ul className="asso-liste-alias">
                {etat.data.associations.filter((a) => a.status === 'ACTIVE' || a.status === 'SUGGESTED')
                  .sort((a, b) => a.name.localeCompare(b.name, 'fr')).map((a) => (
                    <li key={a.id}>
                      <Link href={cheminFiche(a.id)}>{a.name}</Link>{a.status === 'SUGGESTED' ? <span className="lc-hint"> (suggérée)</span> : null}
                      <span className="asso-liste-alias__mots">{a.alias.length === 0 ? <span className="lc-hint">aucun alias</span> : a.alias.map((x) => <span key={x.id} className="asso-puce">{x.label}</span>)}</span>
                    </li>
                  ))}
              </ul>
            )}
      </div>
    </section>
  )
}
